import { useQuery } from '@tanstack/react-query';

import { BaseHTTPError } from '@fe-core/errors';
import {
  getResourcesData,
  resourceTypeToGroup,
  TResourceData,
  TResourceGroupType,
} from '@fe-learner-common';
export const RESOURCE_DATA_QUERY_KEY = 'resourceData';

export type TResources = TResource[];
export type TResource = TResourceData & {
  groupType: TResourceGroupType;
};

const queryFunction = async <T>(
  baseUrl: string,
  participationId: string,
  dataMapper?: DataMapper<T>,
): Promise<T> => {
  const result = await getResourcesData(baseUrl, participationId);
  const resources = result
    // excluded as no renderer exists at this time
    .filter(
      (resource) =>
        resource.componentType !== 'material-flashcard' &&
        resource.componentType !== 'material-questionnaire',
    )
    .map((resource) => ({
      ...resource,
      groupType: resourceTypeToGroup(resource.componentType),
    }));

  if (dataMapper) {
    return dataMapper(resources);
  }

  return resources as T;
};

export const resourcesQueryKey = (participationId: string) => [
  RESOURCE_DATA_QUERY_KEY,
  participationId,
];

type DataMapper<T> = (data: TResources) => T;

type Props<T> = {
  participationId?: string | null;
  apiUrl: string;
  dataMapper?: (data: TResources) => T;
};

export const useResourcesData = <T>({
  participationId,
  apiUrl,
  dataMapper,
}: Props<T>) => {
  if (!participationId) {
    throw new Error('Missing participationId');
  }

  const { data, error, ...rest } = useQuery<T, BaseHTTPError>({
    queryKey: resourcesQueryKey(participationId),
    queryFn: () => queryFunction(apiUrl, participationId, dataMapper),
    retry: false,
    enabled: !!participationId,
    staleTime: Infinity,
  });

  return {
    data,
    error,
    ...rest,
  };
};
